import { useCallback, useEffect, useRef } from "react";
import * as THREE from "three";
import { logger } from "../../utils/logger";

export function useDebugPivotSpheres({
  findJointVizGroup,
  jointLookup,
  jointObjectsRef,
  sceneObjects,
  sceneRef,
  selectedJoint,
  showJoints,
}) {
  const debugPivotRefs = useRef({
    expected: null,
    actual: null,
    origin: null,
    actualGroup: null,
  });

  const ensureDebugSphere = useCallback(
    (color) => {
      const geom = new THREE.SphereGeometry(0.01, 16, 16);
      const mat = new THREE.MeshBasicMaterial({
        color,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.renderOrder = 10000;
      mesh.visible = false;
      sceneRef.current && sceneRef.current.add(mesh);
      return mesh;
    },
    [sceneRef]
  );

  const removeDebugSpheres = useCallback(() => {
    const refs = debugPivotRefs.current;
    ["expected", "origin"].forEach((k) => {
      if (refs[k] && sceneRef.current) sceneRef.current.remove(refs[k]);
      refs[k] = null;
    });
    // For the actual group, restore visibility to showJoints if found.
    if (refs.actualGroup) {
      try {
        refs.actualGroup.visible = showJoints;
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
      refs.actualGroup = null;
    }
  }, [sceneRef, showJoints]);

  const updateDebugSpheresForJoint = useCallback(
    (jointIdentifier) => {
      if (!sceneRef.current || !jointIdentifier || !window.__debugPivotOn) {
        const refs = debugPivotRefs.current;
        if (refs.expected) refs.expected.visible = false;
        if (refs.origin) refs.origin.visible = false;
        if (refs.actualGroup) {
          try {
            refs.actualGroup.visible = showJoints;
          } catch (error) {
            logger.warn("[Scene] Unexpected error during scene update", error);
          }
        }
        return;
      }
      const ref = (jointObjectsRef.current && jointObjectsRef.current[jointIdentifier]) || null;
      if (!ref) return;
      const { bodyGroup, joint } = ref;
      if (!bodyGroup) return;

      const refs = debugPivotRefs.current;
      if (!refs.expected) refs.expected = ensureDebugSphere(0x00ffff);
      if (!refs.origin) refs.origin = ensureDebugSphere(0xffff00);

      // Use the joint viz group instead of an "actual" sphere.
      let vizGroup = refs.actualGroup;
      if (!vizGroup) {
        vizGroup = findJointVizGroup(jointIdentifier);
        refs.actualGroup = vizGroup || null;
      }
      if (vizGroup) vizGroup.visible = true;

      const pivotGroup = bodyGroup.userData?._pivotGroup || null;
      const parentObj = pivotGroup ? pivotGroup.parent : bodyGroup.parent;
      const jointPosLocal = new THREE.Vector3().fromArray(joint.position || [0, 0, 0]);
      let expectedWorld = null;
      if (parentObj && parentObj.localToWorld) {
        expectedWorld = jointPosLocal.clone();
        parentObj.localToWorld(expectedWorld);
      }

      const originWorld = new THREE.Vector3();
      bodyGroup.getWorldPosition(originWorld);

      if (expectedWorld) {
        refs.expected.position.copy(expectedWorld);
        refs.expected.visible = true;
      } else {
        refs.expected.visible = false;
      }
      refs.origin.position.copy(originWorld);
      refs.origin.visible = true;
    },
    [ensureDebugSphere, findJointVizGroup, jointObjectsRef, sceneRef, showJoints]
  );

  useEffect(() => {
    if (!selectedJoint) {
      removeDebugSpheres();
      return;
    }
    updateDebugSpheresForJoint(selectedJoint);
  }, [selectedJoint, jointLookup, sceneObjects, removeDebugSpheres, updateDebugSpheresForJoint]);

  useEffect(() => {
    const onPosUpdated = (e) => {
      const { jointUuid } = e.detail || {};
      if (jointUuid && jointUuid === selectedJoint) {
        updateDebugSpheresForJoint(jointUuid);
      }
    };
    window.addEventListener("joint-pos-updated", onPosUpdated);
    return () => window.removeEventListener("joint-pos-updated", onPosUpdated);
  }, [selectedJoint, updateDebugSpheresForJoint]);
}
